import { useCallback, useRef, useState, type ChangeEvent, type DragEvent } from 'react';
import { flushSync } from 'react-dom';
import { PDFDocument } from 'pdf-lib';
import { Button } from '@/components/ui/Button/Button';
import { IconButton } from '@/components/ui/IconButton/IconButton';
import { DeleteIcon } from '@/components/ui/Icons';
import { Input } from '@/components/ui/Input/Input';
import { parseKeepPages } from './parseKeepPages';
import { splitPdfPages } from './splitPdfPages';
import './PdfSplitterMiniApp.css';

type ProgressState = {
  current: number;
  total: number;
};

function isPdfFile(file: File): boolean {
  const name = file.name.toLowerCase();
  return file.type === 'application/pdf' || name.endsWith('.pdf');
}

function makeSplitFileName(fileName: string): string {
  const base = fileName.replace(/\.pdf$/i, '');
  return `${base}-split.pdf`;
}

function downloadPdf(bytes: Uint8Array, fileName: string): void {
  const blob = new Blob([Uint8Array.from(bytes)], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  URL.revokeObjectURL(url);
}

export function PdfSplitterMiniApp() {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const processingRef = useRef(false);
  const [file, setFile] = useState<File | null>(null);
  const [keepPagesInput, setKeepPagesInput] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [progress, setProgress] = useState<ProgressState | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [resultBytes, setResultBytes] = useState<Uint8Array | null>(null);
  const [resultFileName, setResultFileName] = useState<string | null>(null);
  const busy = progress !== null;
  const canSave = resultBytes !== null && resultFileName !== null;
  const progressPercent =
    progress && progress.total > 0 ? Math.round((progress.current / progress.total) * 100) : 0;

  const invalidateResult = useCallback(() => {
    setResultBytes(null);
    setResultFileName(null);
  }, []);

  const clearFile = useCallback(() => {
    setFile(null);
    setDragActive(false);
    setProgress(null);
    setErrorMessage(null);
    setResultBytes(null);
    setResultFileName(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const selectFile = useCallback(
    (nextFile: File) => {
      if (!isPdfFile(nextFile)) {
        setErrorMessage('Поддерживается только PDF.');
        return;
      }
      setFile(nextFile);
      setErrorMessage(null);
      invalidateResult();
    },
    [invalidateResult]
  );

  const runSplit = useCallback(
    async (selectedFile: File) => {
      if (processingRef.current) return;
      processingRef.current = true;
      flushSync(() => {
        setErrorMessage(null);
        invalidateResult();
        setProgress({ current: 0, total: 0 });
      });
      try {
        const bytes = new Uint8Array(await selectedFile.arrayBuffer());
        const srcDoc = await PDFDocument.load(bytes, { ignoreEncryption: true });
        const pageCount = srcDoc.getPageCount();
        const parsed = parseKeepPages(keepPagesInput, pageCount);
        if (!parsed.ok) {
          setErrorMessage(parsed.errorMessage);
          return;
        }

        setProgress({ current: 0, total: pageCount });
        const result = await splitPdfPages(bytes, parsed.keep, ({ current, total }) => {
          setProgress({ current, total });
        });
        const downloadName = makeSplitFileName(selectedFile.name);
        setResultBytes(result);
        setResultFileName(downloadName);
        downloadPdf(result, downloadName);
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Не удалось обработать PDF.';
        setErrorMessage(message);
      } finally {
        processingRef.current = false;
        setProgress(null);
      }
    },
    [invalidateResult, keepPagesInput]
  );

  const handleInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const nextFile = event.target.files?.[0];
      event.target.value = '';
      if (!nextFile) return;
      selectFile(nextFile);
    },
    [selectFile]
  );

  const handlePickClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleDragOver = useCallback((event: DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(true);
  }, []);

  const handleDragLeave = useCallback((event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const rel = event.relatedTarget as Node | null;
    if (rel && event.currentTarget.contains(rel)) return;
    setDragActive(false);
  }, []);

  const handleDrop = useCallback(
    (event: DragEvent) => {
      event.preventDefault();
      event.stopPropagation();
      setDragActive(false);
      if (busy) return;
      const nextFile = event.dataTransfer.files[0];
      if (!nextFile) return;
      selectFile(nextFile);
    },
    [busy, selectFile]
  );

  const handleSplitClick = useCallback(() => {
    if (!file || busy) return;
    void runSplit(file);
  }, [busy, file, runSplit]);

  const handleKeepPagesChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setKeepPagesInput(event.target.value);
      setErrorMessage(null);
      invalidateResult();
    },
    [invalidateResult]
  );

  const handleSave = useCallback(() => {
    if (!resultBytes || !resultFileName) return;
    downloadPdf(resultBytes, resultFileName);
  }, [resultBytes, resultFileName]);

  return (
    <div className="pdf-splitter-mini-app" aria-busy={busy}>
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,application/pdf"
        style={{ display: 'none' }}
        onChange={handleInputChange}
      />

      <div
        role="button"
        tabIndex={0}
        className={`pdf-splitter-mini-app__drop${dragActive ? ' pdf-splitter-mini-app__drop_drag' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => {
          if (!busy) handlePickClick();
        }}
        onKeyDown={(event) => {
          if (busy) return;
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handlePickClick();
          }
        }}
      >
        {file ? (
          <IconButton
            type="button"
            className="pdf-splitter-mini-app__drop-clear"
            aria-label="Удалить файл"
            size="s"
            variant="danger"
            disabled={busy}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              clearFile();
            }}
          >
            <DeleteIcon />
          </IconButton>
        ) : null}
        {file ? (
          <span className="pdf-splitter-mini-app__file-name" title={file.name}>
            {file.name}
          </span>
        ) : (
          <span>Перетащите PDF сюда или нажмите для выбора</span>
        )}
        <span className="pdf-splitter-mini-app__hint">Только один PDF-файл за раз.</span>
      </div>

      <div className="pdf-splitter-mini-app__keep-field">
        <Input
          placeholder="Укажите страницы, которые не нужно разделять"
          value={keepPagesInput}
          onChange={handleKeepPagesChange}
          disabled={busy}
          fullWidth
        />
      </div>

      {file ? (
        <div className="pdf-splitter-mini-app__actions">
          {canSave && !busy ? (
            <Button type="button" variant="primary" size="s" fullWidth onClick={handleSave}>
              Сохранить
            </Button>
          ) : (
            <Button
              type="button"
              variant="primary"
              size="s"
              fullWidth
              onClick={handleSplitClick}
              disabled={busy}
              aria-busy={busy}
            >
              {busy ? (
                <span className="pdf-splitter-mini-app__btn-busy">
                  <span className="pdf-splitter-mini-app__btn-spinner" aria-hidden="true" />
                  Разделяем файл {progressPercent}%
                </span>
              ) : (
                'Разделить'
              )}
            </Button>
          )}
        </div>
      ) : null}

      {errorMessage ? <p className="pdf-splitter-mini-app__error">{errorMessage}</p> : null}
    </div>
  );
}
